import { useCallback, useEffect, useRef, useState } from "react";
import { DatabaseBackup, Download, FileArchive, Images, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../api";

const MAX_BACKUP_BYTES = 512 * 1024 * 1024;

const formatDate = (value) => {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not available";
  return parsed.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  });
};

const filenameFromHeaders = (headers) => {
  const disposition = headers?.get?.("content-disposition") || "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  try {
    return decodeURIComponent(encoded || plain || "");
  } catch {
    return plain || "";
  }
};

export default function AdminBackups() {
  const inputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const response = await api.getOnce("/api/admin/catalog-backup/status", { cache: "no-store" });
      setStatus(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const response = await api.download("/api/admin/catalog-backup/export", { cache: "no-store" });
      const href = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download =
        filenameFromHeaders(response.headers) ||
        `lalita-soujanya-catalog-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(href), 1000);
      const missingImages = Number(response.headers?.get?.("x-catalog-missing-images") || 0);
      if (missingImages) {
        toast.warning(`Backup downloaded, but ${missingImages} referenced image(s) were already missing on the server.`);
      } else {
        toast.success("Complete catalogue backup downloaded");
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDownloading(false);
    }
  };

  const chooseFile = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return setSelectedFile(null);
    if (!file.name.toLowerCase().endsWith(".zip")) {
      event.target.value = "";
      setSelectedFile(null);
      return toast.error("Select the .zip file downloaded from Catalog Backup");
    }
    if (file.size > MAX_BACKUP_BYTES) {
      event.target.value = "";
      setSelectedFile(null);
      return toast.error("Backup is larger than the 512 MB safety limit");
    }
    setSelectedFile(file);
  };

  const restoreBackup = async () => {
    if (!selectedFile) return toast.error("Choose a catalogue backup ZIP first");
    const confirmed = window.confirm(
      "Restore this catalogue backup? Matching products and categories will be updated, missing ones will be added, and unrelated current records will not be deleted."
    );
    if (!confirmed) return;

    setRestoring(true);
    try {
      const form = new FormData();
      form.append("file", selectedFile, selectedFile.name);
      const response = await api.post("/api/admin/catalog-backup/restore", form);
      const result = response.data;
      const restoreMessage = `Restore complete: ${result.products_created} added, ${result.products_updated} updated, ${result.images_restored} images verified.`;
      if (result.missing_images_in_backup) {
        toast.warning(`${restoreMessage} ${result.missing_images_in_backup} image(s) were already missing when this backup was created.`, { duration: 9000 });
      } else {
        toast.success(restoreMessage);
      }
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await loadStatus();
    } catch (error) {
      toast.error(formatApiError(error), { duration: 9000 });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="admin-page catalog-backup-page">
      <div className="admin-page-head">
        <div>
          <span className="eyebrow">Data protection</span>
          <h1>Catalog Backup.</h1>
        </div>
        <span className="backup-safety-badge"><ShieldCheck size={15} /> Admin only</span>
      </div>

      <div className="backup-summary" aria-live="polite">
        <div>
          <DatabaseBackup size={21} />
          <span>All products</span>
          <strong>{loading ? "—" : status?.products ?? 0}</strong>
        </div>
        <div>
          <FileArchive size={21} />
          <span>Categories</span>
          <strong>{loading ? "—" : status?.categories ?? 0}</strong>
        </div>
        <div>
          <Images size={21} />
          <span>Latest product change</span>
          <strong className="backup-date">{loading ? "Loading…" : formatDate(status?.latest_product_update)}</strong>
        </div>
      </div>

      <div className="backup-workflow-grid">
        <section className="backup-panel backup-export-panel">
          <span className="backup-step">01</span>
          <Download size={26} aria-hidden="true" />
          <h2>Download a complete catalogue copy</h2>
          <p>
            Saves every active and draft product, category placement, size, price, delivery time and locally uploaded
            product/category image into one verified ZIP file.
          </p>
          <button
            className="button dark-button"
            type="button"
            onClick={downloadBackup}
            disabled={downloading || restoring}
            data-testid="download-catalog-backup"
          >
            <Download size={15} /> {downloading ? "Preparing secure ZIP…" : "Download all products"}
          </button>
          <small>Store this downloaded ZIP outside the VPS, preferably in two safe locations.</small>
        </section>

        <section className="backup-panel backup-restore-panel">
          <span className="backup-step">02</span>
          <Upload size={26} aria-hidden="true" />
          <h2>Restore from a saved copy</h2>
          <p>
            The server validates the backup type, version, paths, data and every image checksum before restoration.
            Matching items update safely; missing items return; other current records stay untouched.
          </p>
          <label className={`backup-file-picker ${selectedFile ? "has-file" : ""}`}>
            <input ref={inputRef} type="file" accept=".zip,application/zip" onChange={chooseFile} disabled={restoring} />
            <FileArchive size={18} />
            <span>{selectedFile ? selectedFile.name : "Choose catalogue backup ZIP"}</span>
          </label>
          <button
            className="button outline-button"
            type="button"
            onClick={restoreBackup}
            disabled={!selectedFile || restoring || downloading}
            data-testid="restore-catalog-backup"
          >
            <Upload size={15} /> {restoring ? "Validating and restoring…" : "Restore products safely"}
          </button>
        </section>
      </div>

      <section className="backup-policy-note">
        <ShieldCheck size={20} />
        <div>
          <strong>Private information is excluded.</strong>
          <p>This catalogue ZIP never contains admin passwords, users, customers, orders, API keys or the server .env file.</p>
        </div>
      </section>

      {status?.last_restore && (
        <section className="backup-last-restore">
          <span className="eyebrow">Last successful restore</span>
          <strong>{formatDate(status.last_restore.restored_at)}</strong>
          <p>
            Products: {status.last_restore.products_created} added / {status.last_restore.products_updated} updated ·
            Categories: {status.last_restore.categories_created} added / {status.last_restore.categories_updated} updated ·
            Images: {status.last_restore.images_restored}
          </p>
        </section>
      )}
    </div>
  );
}
